import { Grid, Button, Card, styled } from "@mui/material";
import { paymentSectionsApi } from "api";
import { CreateSectionDto, PaymentMethodEntity, SectionEntity } from "api/generated";
import { CustomDialog } from "components/CustomDialog";
import { H2, H4, H6 } from "components/Typography";
import FlexBox from "components/flexbox/FlexBox";
import { t } from "i18next";
import { FC, useEffect, useState } from "react"
import { Toaster } from "react-hot-toast";
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import AddIcon from '@mui/icons-material/Add';
import PaymentSectionsDataTable from "page-sections/data-table/PaymentSectionsDataTable";
import AddPaymentSectionModal from "page-sections/data-table/dataTableV2/AddPaymentSectionModal";
import AppModal from "components/AppModal";
import SearchInput from "components/input-fields/SearchInput";
import { searchByName } from "utils/utils";
import AddTurnoverModal from "page-sections/data-table/dataTableV2/AddTurnoverModal";
import { useSeason } from "contexts/SeasonContext";

const Sections: FC = () => {
    const [paymentSections, setPaymentSections] = useState<SectionEntity[]>([]);
    const [filteredPaymentSections, setFilteredPaymentSections] = useState<SectionEntity[]>([]);
    const [selectedRows, setSelectedRows] = useState<SectionEntity[]>([]);
    const [openAddModal, setOpenAddModal] = useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [openErrorDeleteDialog, setOpenErrorDeleteDialog] = useState(false);
    const [openAddTurnoverModal, setOpenAddTurnoverModal] = useState(false);
    const [searchValue, setSearchValue] = useState("");
    const [data, setData] = useState<SectionEntity>();
    const {seasonId} = useSeason();

    const ButtonWrapper = styled(FlexBox)(({ theme }) => ({
        [theme.breakpoints.down(500)]: {
            marginTop: 10,
            width: '100%',
            flexDirection: 'column-reverse',
            '& > .MuiBox-root': {
                width: '100%',
                margin: '10px 0',
                alignItems: 'center',
                flexDirection: 'column',
            },
            '& .MuiButton-root': { minWidth: '100%' },
        },
    }));

    const fetchData = async () => {
        const ps = await paymentSectionsApi.findAll(seasonId);
        setPaymentSections(ps.data.sort((a, b) => { 
            return a.id - b.id;
        }));
    }

    const addSection = async (paymentSection: CreateSectionDto, paymentMethods: PaymentMethodEntity[]) => {
        if (data === undefined) {
            await paymentSectionsApi.create({
                paymentMethods,
                sectionData: paymentSection
            });

        } else {
            await paymentSectionsApi.update(String(data.id), {
                sectionData: paymentSection,
                paymentMethods
            });

        }
        fetchData();
        setOpenAddModal(false);
        setData(undefined);
    }

    const handleDelete = async () => {
        for (const section of selectedRows) {
            try {
                await paymentSectionsApi.remove(String(filteredPaymentSections[section.id].id));
            } catch (e: any) {
                if (e.response.status === 405) {
                    setOpenErrorDeleteDialog(true);
                }
            }
        }
        setOpenDeleteDialog(false);
        setSelectedRows([]);
        fetchData();
    }

    const handleEdit = (data: SectionEntity) => {
        setData(data)
        setOpenAddModal(true);
    }

    const handleRowSelect = (rowArr: SectionEntity[]) => {
        setSelectedRows(rowArr);
    }

    const handleOpenAddTurnover = (data: SectionEntity) => {
        setData(data)
        setOpenAddTurnoverModal(true)
    }

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        setFilteredPaymentSections(searchByName(paymentSections, searchValue))
    }, [paymentSections, searchValue])

    return <Grid container>
        <Toaster />
        <CustomDialog
            open={openDeleteDialog}
            onClose={() => { setOpenDeleteDialog(false) }}
            onConfirm={handleDelete}
            title={t('dialog.title', {
                item: t('amountSection.item'),
            })}
            content={t('dialog.content')}
            icon={<DeleteForeverIcon />}
            confirmText={t('dialog.confirm')}
            confirmColor='error'
            cancelColor='primary'
        />
        <Grid item xs={12}>
            <SearchInput
                bordered={false}
                placeholder={t('common.tables.button.searchItems', {
                    items: t('amountSection.item'),
                })}
                onChange={(e) => {
                    setSearchValue(e.target.value);
                }}
            />
            <ButtonWrapper justifyContent={'end'}>
                {selectedRows.length > 0 && (
                    <FlexBox alignItems='center' mr={2}>
                        <H6 mr={1}>
                            {t('common.tables.select.numSelected', {
                                num: selectedRows.length,
                            })}
                        </H6>
                        <Button
                            size='small'
                            color='error'
                            variant='contained'
                            onClick={() => { setOpenDeleteDialog(true) }}
                            sx={{ color: 'common.white' }}
                        >
                            {t('common.tables.select.deleteSelected')}
                        </Button>
                    </FlexBox>
                )}
                <Button
                    startIcon={<AddIcon />}
                    variant='contained'
                    size='small'
                    onClick={() => { setOpenAddModal(true) }}
                >
                    {t('common.tables.button.addItem', {
                        item: t('amountSection.item'),
                    })}
                </Button>
            </ButtonWrapper>
        </Grid>
        <Grid item xs={12}>
            <Card sx={{ width: '100%', marginTop: 3 }}>
                <PaymentSectionsDataTable
                    data={filteredPaymentSections}
                    handleRowSelect={handleRowSelect}
                    editCallback={handleEdit}
                    openAddTurnoverModal={handleOpenAddTurnover}
                />
            </Card>
        </Grid>
        <AddTurnoverModal
            section={data}
            isController={false}
            isOwner={true} // Hardcoded because sections page should be accessible only by owners
            open={openAddTurnoverModal}
            onClose={() => { setOpenAddTurnoverModal(false) }}
        />
        <AddPaymentSectionModal open={openAddModal} onClose={() => { setOpenAddModal(false); setData(undefined); }} onSubmitAction={addSection} data={data} />
        <AppModal open={openErrorDeleteDialog} handleClose={() => { setOpenErrorDeleteDialog(false) }}>
            <H2>{t('delete.error')}</H2>
            <H4 marginTop={2}>{t('section.error.delete')}</H4>
            <ButtonWrapper justifyContent={'end'}>
                <Button size='small' variant='contained' color='error' onClick={() => { setOpenErrorDeleteDialog(false) }}>{t('dialog.confirm')}</Button>
            </ButtonWrapper>
        </AppModal>
    </Grid >
}

export default Sections;